/**
 * ReservationMessagesTab — Timeline de comunicacions d'una reserva.
 *
 * Eix central: reservationId + phoneNumber (NO clientId).
 *
 * Mostra, agrupat per dia (plans/whatsapp-capture.md A4.1):
 *   - CommunicationLog entries: missatges de WhatsApp importats i d'OTA (Beds24) en una
 *     línia expandible amb la direcció, i converses enganxades via PasteConversationDialog.
 *     Paginats de 100 en 100 («Carregar anteriors»): una reserva pot tenir milers de missatges.
 *   - MessagingTask entries (missatges enviats via el sistema)
 *   - UseCase entries (casos escrits sobre la reserva) — 🆕 F3.5, **només lectura**
 *
 * Inclou botó per obrir PasteConversationDialog amb reservationId i el botó de WhatsApp
 * (missatge lliure) per escriure a l'hoste en dos clics. Si algun missatge carregat és d'una
 * conversa de l'inbox, «Obre a l'inbox» porta a `/whatsapp/<id>` (plans/whatsapp-inbox.md F3).
 */

import { useState, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { ClipboardPaste, Inbox, Loader2, Phone, RefreshCw, NotebookPen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { useCommunicationLogsByReservation } from '@/hooks/useCommunicationLogs';
import { useMessagingTasks, messagingKeys } from '@/hooks/useMessagingTask';
import { useUseCases } from '@/hooks/useUseCases';
import { caseExcerpt } from '@/lib/use-cases';
import { langToLocale } from '@/lib/dates';
import { TOPIC_LABEL } from '@/components/use-cases/UseCaseForm';
import { PasteConversationDialog } from './PasteConversationDialog';
import { WhatsAppActionButton } from './WhatsAppActionButton';
import {
  ChannelFilterChips,
  CommunicationLogEntry,
  EmptyTimeline,
  MessageTimeline,
  MessagingTaskEntry,
  PasteHint,
  buildTimeline,
  emptyTimelineText,
  type TimelineItem,
} from './MessageTimeline';
import type { CommunicationLogChannelFilter, UseCase } from '@guilleumes/types';

const locale = langToLocale('ca');

export interface ReservationMessagesTabProps {
  /** Reservation ID — eix central de la missatgeria */
  reservationId: string;
  /** Telèfon del hoste (opcional, per mostrar context) */
  guestPhone?: string;
}

/**
 * Un cas escrit, vist des de la línia de temps.
 *
 * 🆕 F3.5. Aquí **no s'escriu**: un cas es crea només des de la secció "Casos" de la
 * fitxa, perquè hi hagi un sol lloc on mirar si una cosa s'ha desat. El que aporta la
 * línia de temps és el moment: veure que el cas es va escriure l'endemà del missatge
 * que el va provocar explica la meitat de la història.
 */
function UseCaseEntry({ useCase }: { useCase: UseCase }) {
  return (
    <Card className="border-l-4 border-l-emerald-400">
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <NotebookPen className="h-4 w-4 shrink-0 text-emerald-500" />
            <div className="min-w-0 flex-1">
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <p className="text-sm font-medium">Cas escrit</p>
                {useCase.topic ? (
                  <Badge variant="outline" className="text-xs">
                    {TOPIC_LABEL[useCase.topic]}
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-xs text-muted-foreground">
                    sense tema
                  </Badge>
                )}
              </div>
              <p className="mt-1 break-words text-xs text-muted-foreground [overflow-wrap:anywhere]">
                {caseExcerpt(useCase.whatHappened)}
              </p>
            </div>
          </div>
          <div className="shrink-0 whitespace-nowrap text-right">
            <p className="text-xs text-muted-foreground">
              {format(new Date(useCase.createdAt), 'dd/MM/yyyy HH:mm', { locale })}
            </p>
            {useCase.authorName && (
              <p className="text-xs text-muted-foreground">Per: {useCase.authorName}</p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function ReservationMessagesTab({ reservationId, guestPhone }: ReservationMessagesTabProps) {
  const [pasteDialogOpen, setPasteDialogOpen] = useState(false);
  const [channel, setChannel] = useState<CommunicationLogChannelFilter | undefined>(undefined);
  const queryClient = useQueryClient();

  // Eix central: reservationId (NO clientId)
  const {
    data: logPages,
    isLoading: logsLoading,
    refetch: refetchLogs,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useCommunicationLogsByReservation(reservationId, { channel });
  const { data: allTasks, isLoading: tasksLoading } = useMessagingTasks(reservationId);
  // 🆕 F3.5 — Els casos d'aquesta reserva, de lectura. Mateixa clau que la secció
  // "Casos" de la fitxa: escriure'n un des d'allà refresca també aquesta llista.
  const { data: useCasesData, isLoading: useCasesLoading } = useUseCases({ reservationId });

  const isLoading = logsLoading || tasksLoading || useCasesLoading;
  // Conversa de l'inbox del missatge més recent carregat que en tingui.
  const inboxConversationId =
    logPages?.pages.flatMap((page) => page.items).find((log) => log.whatsappConversationId)
      ?.whatsappConversationId ?? null;

  const handleRefresh = () => {
    refetchLogs();
    queryClient.invalidateQueries({ queryKey: messagingKeys.tasks(reservationId) });
    queryClient.invalidateQueries({ queryKey: ['use-cases'] });
  };

  // Build unified timeline centrada en la reserva
  const { timeline, logCount } = useMemo(() => {
    const logItems: TimelineItem[] = (logPages?.pages ?? []).flatMap((page) =>
      page.items.map((log) => ({
        id: `log-${log.id}`,
        date: new Date(log.loggedAt),
        node: <CommunicationLogEntry log={log} />,
      })),
    );

    const others: TimelineItem[] = [];
    // Les tasques són missatges de WhatsApp: surten a «Tots» i a «WhatsApp».
    if (allTasks && (!channel || channel === 'WHATSAPP')) {
      for (const task of allTasks.filter((t) => t.reservationId === reservationId)) {
        others.push({
          id: `task-${task.id}`,
          date: new Date(task.sentAt || task.createdAt),
          node: <MessagingTaskEntry task={task} />,
        });
      }
    }
    // Els casos no són d'un canal: només a «Tots».
    if (useCasesData && !channel) {
      for (const useCase of useCasesData.data) {
        others.push({
          id: `use-case-${useCase.id}`,
          date: new Date(useCase.createdAt),
          node: <UseCaseEntry useCase={useCase} />,
        });
      }
    }

    return {
      timeline: buildTimeline(logItems, others, !!hasNextPage),
      logCount: logItems.length,
    };
  }, [logPages, allTasks, useCasesData, reservationId, channel, hasNextPage]);

  return (
    <div className="min-w-0 space-y-3">
      {/* Action bar */}
      {/* Sense títol propi: la secció de la fitxa ja en té. */}
      <div className="flex min-w-0 flex-wrap items-center justify-between gap-2">
        {guestPhone && (
          <div className="flex min-w-0 items-center gap-1 text-xs text-muted-foreground">
            <Phone className="h-3 w-3 shrink-0" />
            <span className="truncate">{guestPhone}</span>
          </div>
        )}
        <div className="ml-auto flex items-center gap-1">
          <Button
            size="sm"
            variant="ghost"
            onClick={handleRefresh}
            disabled={isLoading}
            className="h-8 w-8 p-0"
            title="Actualitzar historial"
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
          {inboxConversationId && (
            <Button asChild size="sm" variant="outline" className="gap-2">
              <Link to={`/whatsapp/${inboxConversationId}`}>
                <Inbox className="h-4 w-4" />
                Obre a l&apos;inbox
              </Link>
            </Button>
          )}
          <WhatsAppActionButton
            reservationId={reservationId}
            messageKind="CUSTOM"
            size="sm"
            variant="outline"
          />
          <Button
            size="sm"
            variant="outline"
            onClick={() => setPasteDialogOpen(true)}
            className="gap-2"
          >
            <ClipboardPaste className="h-4 w-4" />
            Enganxar WA
          </Button>
        </div>
      </div>

      <ChannelFilterChips value={channel} onChange={setChannel} />

      <Separator />

      {/* Timeline */}
      {isLoading ? (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          <span className="ml-2 text-sm text-muted-foreground">Carregant historial...</span>
        </div>
      ) : timeline.length === 0 ? (
        <EmptyTimeline channel={channel} owner="reservation" className="py-8" />
      ) : (
        // A4.5: sense contenidor amb scroll propi; fa scroll la fitxa (a mòbil hi havia dos scrolls encaixats).
        <>
          <MessageTimeline
            items={timeline}
            hasNextPage={!!hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            onLoadMore={() => fetchNextPage()}
          />
          {/* Tasques o casos però cap missatge: sense rètol de buit, però amb la invitació a enganxar. */}
          {logCount === 0 && emptyTimelineText(channel, 'reservation').pasteHint && (
            <PasteHint className="text-center" />
          )}
        </>
      )}

      {/* PasteConversationDialog — passa reservationId, NO clientId */}
      <PasteConversationDialog
        reservationId={reservationId}
        open={pasteDialogOpen}
        onOpenChange={setPasteDialogOpen}
      />
    </div>
  );
}
